<script setup>
import VCancel from "@/Components/VCancel.vue";
import FosteringContactFormLayout from "@/Layouts/FosteringContactFormLayout.vue";
import AdditionalComments from "@/Pages/SouthEastBrochure/Partials/AdditionalComments.vue";
import ContactDetails from "@/Pages/SouthEastBrochure/Partials/ContactDetails.vue";
import Details from "@/Pages/SouthEastBrochure/Partials/Details.vue";
import Footer from "@/Pages/SouthEastBrochure/Partials/Footer.vue";
import FosteringExperience from "@/Pages/SouthEastBrochure/Partials/FosteringExperience.vue";
import Location from "@/Pages/SouthEastBrochure/Partials/Location.vue";
import { Head, useForm, usePage } from '@inertiajs/vue3';

const { headerLogo, isLAReferral, providedLocalAuthority } = usePage().props

const form = useForm({
    forename: null,
    surname: null,
    phoneNumber: null,
    email: null,

    postcode: null,
    localAuthority: null,

    experience: null,
    experience_id: null,
    experienceDetails: null,

    contactAnyTime: null,
    termsAndConditions: null,
    contactTimeSchedule: null,

    initialComments: null,
    firstHeard: null,
    firstHeardOther: null,
})

const setSubmitButtonDisabledState = (disabled) => {
    if (disabled) {
        document.getElementById('submit-button').setAttribute('disabled', true);
    } else {
        document.getElementById('submit-button').removeAttribute('disabled');
    }
}

const submit = () => {
    setSubmitButtonDisabledState(true);
    if (!isLAReferral) {
        form.post(route('south-east.form.submit'), {
            onSuccess: () => setSubmitButtonDisabledState(false),
            onError: () => setSubmitButtonDisabledState(false),
        })
    } else {
        if (!form.localAuthority) {
            form.localAuthority = providedLocalAuthority.id;
        }
        form.post(route('south-east.la-referral.form.submit', providedLocalAuthority.slug), {
            onSuccess: () => setSubmitButtonDisabledState(false),
            onError: () => setSubmitButtonDisabledState(false),
        })
    }
}

const cancel = () => {
    if (isLAReferral && providedLocalAuthority.la_website_url_cancel) {
        window.location = providedLocalAuthority.la_website_url_cancel;
    } else {
        window.location.href = 'https://www.lafosteringse.org.uk'
    }
}

const telephoneNumber = import.meta.env.VITE_SE_FOOTER_PHONE_NUMBER || '';
</script>

<template>
    <Head title="Fostering - South East"/>
    <FosteringContactFormLayout layout="SE" title="Contact us" header-colour="white" footer-colour="footer">
        <template v-slot:header >
            <div class="">
                <template v-if="!isLAReferral">
                    <a href="https://www.lafosteringse.org.uk/"
                       tabindex="1"
                       autofocus
                    >
                        <img
                            :src="headerLogo"
                            alt="Fostering South East Logo"
                            aria-label="Fostering South East Logo"
                            class="py-4 px-4 d-none d-md-block"
                            height="148"
                        />
                        <img
                            :src="headerLogo"
                            alt="Fostering South East Logo"
                            aria-label="Fostering South East Logo"
                            class="py-4 px-4 d-sm-block d-md-none"
                            width="225"
                        />
                    </a>
                </template>
                <template v-else>
                    <img
                        :src="headerLogo"
                        alt="Fostering South East Logo"
                        aria-label="Fostering South East Logo"
                        class="pa-4 d-none d-md-block"
                        height="auto"
                        width="284"
                    />
                    <img
                        :src="headerLogo"
                        alt="Fostering South East Logo"
                        aria-label="Fostering South East Logo"
                        class="pa-4 d-sm-block d-md-none"
                        width="225"
                    />
                </template>
            </div>
        </template>
        <template v-slot:default>
            <form @submit.prevent="submit">
                <v-row>
                    <v-col cols="12" class="pb-0">
                        <p class="mb-6" tabindex="4">
                            Whether you’ve just started thinking about fostering or you’re ready to begin your application to become a foster carer, we’d love to hear from you. Please get in touch with our friendly recruitment team and we’ll explain everything you need to know about becoming a local authority foster carer in the South East.</p>
                        <p class="mb-6" tabindex="5">
                            You can contact us by filling in our <strong>online enquiry form</strong> below.
                        </p>
                        <p class="mb-6" tabindex="6">
                            Alternatively, you can call us on <strong><a class="se-hyperlink-bg" :href="'tel:' + telephoneNumber" target="_blank">
                                {{ telephoneNumber }}
                            </a></strong> or send an email to <a href="mailto:info@lafosteringse.org.uk" tabindex="6" class="se-hyperlink-bg">info@lafosteringse.org.uk</a>.
                        </p>
                        <p class="mt-3" tabindex="7">
                            <small>
                                * indicates a required field
                            </small>
                        </p>
                    </v-col>
                    <v-col cols="12" md="4" >
                        <Details :form="form" tabindex="0"/>
                    </v-col>
                    <v-col cols="12" md="4" lg="4" xl="4">
                        <v-row>
                            <v-col cols="12">
                                <Location :form="form" tabindex="0"/>
                            </v-col>
                            <v-col cols="12">
                                <FosteringExperience :form="form" tabindex="0"/>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="12" md="4" lg="4" xl="4">
                        <v-row>
                            <v-col cols="12">
                                <ContactDetails :form="form" tabindex="0"/>
                            </v-col>
                            <v-col cols="12">
                                <AdditionalComments :form="form" tabindex="0"/>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <p tabindex="0">
                            Local Authority Fostering South East is a partnership of 20 local authorities from across the region with a shared common goal –
                            to recruit and support more amazing foster carers able to provide safe, loving, and local homes for children in our communities.
                            For more information, visit the <a class="se-hyperlink-bg" target="_blank" tabindex="0" href="https://www.lafosteringse.org.uk/who-we-are/">about us section of our website <v-icon size="12">far fa-arrow-up-right-from-square</v-icon> </a>.
                        </p>
                    </v-col>
                </v-row>
                <v-row class="d-flex">
                    <v-col cols="6">
                        <v-cancel @cancel="cancel()"/>
                    </v-col>
                    <v-col cols="6" class="d-flex justify-end">
                        <v-btn type="submit" id="submit-button" text="Submit"/>
                    </v-col>
                </v-row>
            </form>
        </template>
        <template v-slot:footer>
            <Footer/>
        </template>
    </FosteringContactFormLayout>
</template>

<style scoped>

</style>

<style>

</style>
